/**
 * Energy context (D14, §13.2 Calm mode). `energy` is CALM when ANY of these holds:
 *  - the OS asks for reduced motion (`prefers-reduced-motion: reduce`),
 *  - the lottery config sets the calm switch (`lotteryCalm`),
 *  - the participant toggled calm mode themselves (persisted in localStorage).
 * A participant may also force FULL over the OS preference only through their own explicit toggle.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type EnergyLevel = 'FULL' | 'CALM';
export type UserEnergyPreference = EnergyLevel | 'SYSTEM';

export const ENERGY_STORAGE_KEY = 'windrop:energy';

export interface EnergyContextValue {
  /** Effective energy level after combining all sources. Every preset and mechanic reads this. */
  energy: EnergyLevel;
  calm: boolean;
  /** What the participant chose (SYSTEM = follow the OS and the lottery). */
  userPreference: UserEnergyPreference;
  setUserPreference: (pref: UserEnergyPreference) => void;
  /** OS preference. */
  prefersReducedMotion: boolean;
  /** Lottery-level calm switch from config. */
  lotteryCalm: boolean;
  /** Sound is off by default everywhere and opt-in per participant (§13.2). */
  soundEnabled: boolean;
  setSoundEnabled: (on: boolean) => void;
  /** Haptics are optional and never the sole feedback. */
  hapticsEnabled: boolean;
  setHapticsEnabled: (on: boolean) => void;
}

const EnergyContext = createContext<EnergyContextValue | null>(null);

function readStorage<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = globalThis.localStorage?.getItem(key);
    return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key: string, value: string) {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* private mode etc. — preference just does not persist */
  }
}

function useMediaQuery(query: string): boolean {
  const get = () =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

export interface MotionProviderProps {
  children: ReactNode;
  /** Lottery-level calm switch (config). */
  lotteryCalm?: boolean;
  /** Test/SSR override: forces the effective energy regardless of sources. */
  forceEnergy?: EnergyLevel;
  /** Element that receives `data-energy` so CSS durations collapse to 0 in calm mode. Defaults to <html>. */
  root?: HTMLElement | null;
}

export function MotionProvider({ children, lotteryCalm = false, forceEnergy, root }: MotionProviderProps) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [userPreference, setPref] = useState<UserEnergyPreference>(() =>
    readStorage(ENERGY_STORAGE_KEY, ['FULL', 'CALM', 'SYSTEM'] as const, 'SYSTEM'),
  );
  const [soundEnabled, setSound] = useState(
    () => readStorage(`${ENERGY_STORAGE_KEY}:sound`, ['on', 'off'], 'off') === 'on',
  );
  const [hapticsEnabled, setHaptics] = useState(
    () => readStorage(`${ENERGY_STORAGE_KEY}:haptics`, ['on', 'off'], 'on') === 'on',
  );

  const energy: EnergyLevel = useMemo(() => {
    if (forceEnergy) return forceEnergy;
    if (userPreference === 'CALM') return 'CALM';
    if (lotteryCalm) return 'CALM';
    if (userPreference === 'FULL') return 'FULL';
    return prefersReducedMotion ? 'CALM' : 'FULL';
  }, [forceEnergy, userPreference, lotteryCalm, prefersReducedMotion]);

  useEffect(() => {
    const el = root ?? (typeof document !== 'undefined' ? document.documentElement : null);
    if (!el) return;
    el.setAttribute('data-energy', energy);
    return () => {
      el.removeAttribute('data-energy');
    };
  }, [energy, root]);

  const setUserPreference = useCallback((pref: UserEnergyPreference) => {
    setPref(pref);
    writeStorage(ENERGY_STORAGE_KEY, pref);
  }, []);
  const setSoundEnabled = useCallback((on: boolean) => {
    setSound(on);
    writeStorage(`${ENERGY_STORAGE_KEY}:sound`, on ? 'on' : 'off');
  }, []);
  const setHapticsEnabled = useCallback((on: boolean) => {
    setHaptics(on);
    writeStorage(`${ENERGY_STORAGE_KEY}:haptics`, on ? 'on' : 'off');
  }, []);

  const value = useMemo<EnergyContextValue>(
    () => ({
      energy,
      calm: energy === 'CALM',
      userPreference,
      setUserPreference,
      prefersReducedMotion,
      lotteryCalm,
      soundEnabled,
      setSoundEnabled,
      hapticsEnabled,
      setHapticsEnabled,
    }),
    [
      energy,
      userPreference,
      setUserPreference,
      prefersReducedMotion,
      lotteryCalm,
      soundEnabled,
      setSoundEnabled,
      hapticsEnabled,
      setHapticsEnabled,
    ],
  );
  return <EnergyContext.Provider value={value}>{children}</EnergyContext.Provider>;
}

const fallback: EnergyContextValue = {
  energy: 'CALM',
  calm: true,
  userPreference: 'SYSTEM',
  setUserPreference: () => {},
  prefersReducedMotion: true,
  lotteryCalm: false,
  soundEnabled: false,
  setSoundEnabled: () => {},
  hapticsEnabled: false,
  setHapticsEnabled: () => {},
};

/** Without a provider the kit behaves as CALM — the safe default (§1.2 Accessible excitement). */
export function useEnergy(): EnergyContextValue {
  return useContext(EnergyContext) ?? fallback;
}
